import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Grid } from '@material-ui/core';
import { NavLink as RouterNavLink, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { goBack } from 'connected-react-router';

import StandardLayout from 'components/Layouts/Standard';
import useQuery from 'hooks/useQuery';
import {
  activateChannelClean,
  activateChannelRequest,
  selectActivateChannelValidationError,
  selectChannelTimeout,
  selectSettingsIsLoading,
  verifyChannelRequest,
} from 'store/settings';
import { ChannelType } from 'types/settings';
import { ROUTES } from 'constants/routes';

import Typography from '#web-components/components/Typography';
import Input from '#web-components/components/FormControls/Input';
import Button, { ButtonVariants } from '#web-components/components/Button';
import InlineButton from '#web-components/components/InlineButton';
import { EmailIcon } from '#web-components/components/Icons';
import useTimer from '#web-components/hooks/useTimer';
import FlashMessage from '#web-components/components/FlashMessage';

import styles from './ProfileConfirmChannel.styles';

const MAX_CODE_LENGTH = 6;

interface Params {
  channel: ChannelType;
}

const useStyles = makeStyles(styles, { name: 'ProfileConfirmChannel' });

export default function ProfileConfirmChannel() {
  const dispatch = useDispatch();
  const params = useParams<Params>();
  const { t, i18n } = useTranslation('pages', { keyPrefix: 'confirmChannel' });
  const classes = useStyles();
  const isLoading = useSelector(selectSettingsIsLoading);
  const codeValidationError = useSelector(selectActivateChannelValidationError);
  const timeout = useSelector(selectChannelTimeout);
  const addressQuery = useQuery().get('address');
  const [code, onCodeChange] = useState<{ value: string }>({ value: '' });
  const [codeError, onCodeErrorChange] = useState<string>(codeValidationError || '');
  const { channel } = params;
  const address = addressQuery || '';
  const { time, timeString, clear: clearTimeout } = useTimer(timeout, i18n.language);

  const handleSubmit = (value: string) => {
    dispatch(activateChannelRequest({ channel, address, verificationCode: value }));
  };

  const handleCodeChange = (value: string | number | null) => {
    const newCode = (value as string || '').substring(0, MAX_CODE_LENGTH);

    if (newCode.length === MAX_CODE_LENGTH) {
      handleSubmit(newCode);
    }

    onCodeChange({
      value: newCode,
    });
    onCodeErrorChange('');
  };

  const onCancel = useCallback((event) => {
    event.preventDefault();
    dispatch(goBack());
  }, [dispatch]);

  const onReload = useCallback(() => {
    dispatch(activateChannelClean());
    onCodeChange({ value: '' });
    if (address) {
      dispatch(verifyChannelRequest({
        channel,
        address,
      }));
    }
  }, [channel, address, dispatch]);

  const onRefresh = useCallback(() => {
    onReload();
    clearTimeout();
  }, [onReload, clearTimeout]);

  useEffect(() => {
    clearTimeout();
  }, [clearTimeout, timeout]);

  useEffect(() => {
    onCodeErrorChange(codeValidationError || '');
  }, [codeValidationError]);

  useEffect(() => {
    onReload();
  }, [onReload]);

  return (
    <StandardLayout
      backLink={{
        title: t('backToProfile'),
        path: ROUTES.PROFILE,
        RouterNavLink,
      }}
      title={t(`${channel}.title`)}
      isLoading={isLoading}
    >
      <form className={classes.root}>
        <Grid container>
          <Grid item sm={8}>
            {
              channel === ChannelType.EMAIL && (
                <FlashMessage
                  status="info"
                  title={t(`${channel}.sidebarTitle`)}
                  message={t(`${channel}.sidebarDescription`)}
                />
              )
            }
            <Typography variant="h6" className={classes.info}>
              { t(`${channel}.codeSent`, { address }) }
            </Typography>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item sm={5}>
            <Input
              name="otp_code"
              id="otp_code_input"
              label={t(`${channel}.codeInput.label`)}
              placeholder={t(`${channel}.codeInput.placeholder`, { number: MAX_CODE_LENGTH })}
              withNumberFormat={{ enabled: true, parseToNumber: false, leadingZeros: true }}
              value={code.value}
              onChange={handleCodeChange}
              error={codeError ? { message: codeError } : undefined}
            />
            {time !== 0 && (
              <Typography variant="textTiny" className={classes.timeoutMessage}>
                {t('sendAgainTimeoutMessage', { time: timeString })}
              </Typography>
            )}
            {time === 0 && (
              <InlineButton
                size="small"
                classes={{
                  link: classes.refreshButton,
                }}
                onLinkClick={onRefresh}
                leftIcon={<EmailIcon />}
              >
                {t('sendAgain')}
              </InlineButton>
            )}
          </Grid>
        </Grid>

        <div className={classes.buttonContainer}>
          <Button variant={ButtonVariants.secondary} onClick={onCancel} size="large">
            {t('cancelButton')}
          </Button>
        </div>
      </form>
    </StandardLayout>
  );
}
